import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import {
  Globe, Plus, Trash2, CheckCircle2, Circle, MinusCircle, Sparkles, FileDown, Factory,
  AlertTriangle, MessageSquare, ArrowLeft, Package, Leaf, ShieldCheck, FileText,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABEL = {
  draft: "Draft", data_collection: "Data collection", calculated: "Calculated", in_review: "In review",
  approved: "Approved", submitted: "Submitted", query: "Query", resubmitted: "Resubmitted", archived: "Archived",
};
const STATUS_TONE = {
  draft: "outline", data_collection: "outline", calculated: "outline", in_review: "secondary",
  approved: "default", submitted: "default", query: "destructive", resubmitted: "secondary", archived: "outline",
};

function StatCard({ label, value, unit, icon: Icon, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className="font-mono text-2xl font-bold tracking-tight">{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
    </div>
  );
}

/* ----------------------------- create dialog ----------------------------- */
function CreateCaseDialog({ refData, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", principal: "", country: "", reporting_period: "", role: "", obligation: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name) return toast.error("Case name required");
    setBusy(true);
    try {
      const r = await api.post("/cbam/cases", {
        name: form.name,
        eu_principal: { name: form.principal, country: form.country },
        reporting_period: form.reporting_period, supply_chain_role: form.role, obligation_source: form.obligation,
      });
      setOpen(false); setForm({ name: "", principal: "", country: "", reporting_period: "", role: "", obligation: "" });
      onCreated(r.data.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="primary" data-testid="cbam-new-case-btn"><Plus className="h-4 w-4" /> New CBAM case</Button></DialogTrigger>
      <DialogContent data-testid="cbam-create-dialog">
        <DialogHeader><DialogTitle className="font-display">New CBAM case</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="label-eyebrow text-[10px]">Case name</label><Input data-testid="cbam-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. EuroSteel GmbH — CBAM request FY2024" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-eyebrow text-[10px]">EU principal</label><Input data-testid="cbam-input-principal" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} placeholder="Importer / customer" /></div>
            <div><label className="label-eyebrow text-[10px]">Country</label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Germany" /></div>
          </div>
          <div><label className="label-eyebrow text-[10px]">Reporting period</label><Input value={form.reporting_period} onChange={(e) => setForm({ ...form, reporting_period: e.target.value })} placeholder="2024 (Jan–Dec)" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-eyebrow text-[10px]">Supply-chain role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(refData?.supply_chain_roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="label-eyebrow text-[10px]">Obligation source</label>
              <Select value={form.obligation} onValueChange={(v) => setForm({ ...form, obligation: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(refData?.obligation_sources || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-2"><Button variant="primary" onClick={submit} disabled={busy} data-testid="cbam-create-submit">{busy ? "Creating…" : "Create case"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- product dialog ----------------------------- */
function ProductDialog({ caseId, refData, product, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const blank = { name: "", product_code: "", cn_code: "", cbam_sector: "", uom: "tonne", production_route: "",
    production_volume_t: 0, qty_to_eu_t: 0, electricity_mwh: 0, grid_factor_kg_kwh: refData?.sg_grid_factor || 0.4168,
    direct_emissions_tco2e: 0, allocation_method: "" };
  const [f, setF] = useState(product || blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(product || blank); /* eslint-disable-next-line */ }, [open]);
  const num = (k, v) => setF({ ...f, [k]: v === "" ? "" : Number(v) });
  const save = async () => {
    if (!f.name) return toast.error("Product name required");
    setBusy(true);
    try {
      const r = await api.post(`/cbam/cases/${caseId}/products`, { ...f, id: product?.id });
      setOpen(false); onSaved(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="cbam-product-dialog">
        <DialogHeader><DialogTitle className="font-display">{product ? "Edit" : "Add"} CBAM product</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="col-span-2"><label className="label-eyebrow text-[10px]">Product name</label><Input data-testid="cbam-prod-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="label-eyebrow text-[10px]">Product code</label><Input value={f.product_code || ""} onChange={(e) => setF({ ...f, product_code: e.target.value })} /></div>
          <div><label className="label-eyebrow text-[10px]">CN / HS code</label><Input data-testid="cbam-prod-cn" value={f.cn_code || ""} onChange={(e) => setF({ ...f, cn_code: e.target.value })} placeholder="e.g. 7308.90" /></div>
          <div><label className="label-eyebrow text-[10px]">CBAM sector</label>
            <Select value={f.cbam_sector || ""} onValueChange={(v) => setF({ ...f, cbam_sector: v })}>
              <SelectTrigger data-testid="cbam-prod-sector"><SelectValue placeholder="Select sector" /></SelectTrigger>
              <SelectContent>{(refData?.sector_names || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="label-eyebrow text-[10px]">Unit of measure</label><Input value={f.uom || "tonne"} onChange={(e) => setF({ ...f, uom: e.target.value })} /></div>
          <div className="col-span-2"><label className="label-eyebrow text-[10px]">Production route</label><Input value={f.production_route || ""} onChange={(e) => setF({ ...f, production_route: e.target.value })} /></div>
          <div><label className="label-eyebrow text-[10px]">Production volume (t)</label><Input type="number" value={f.production_volume_t} onChange={(e) => num("production_volume_t", e.target.value)} /></div>
          <div><label className="label-eyebrow text-[10px]">Quantity to EU (t)</label><Input data-testid="cbam-prod-qty" type="number" value={f.qty_to_eu_t} onChange={(e) => num("qty_to_eu_t", e.target.value)} /></div>
          <div><label className="label-eyebrow text-[10px]">Direct emissions allocated (tCO₂e)</label><Input data-testid="cbam-prod-direct" type="number" value={f.direct_emissions_tco2e} onChange={(e) => num("direct_emissions_tco2e", e.target.value)} /></div>
          <div><label className="label-eyebrow text-[10px]">Electricity allocated (MWh)</label><Input type="number" value={f.electricity_mwh} onChange={(e) => num("electricity_mwh", e.target.value)} /></div>
          <div><label className="label-eyebrow text-[10px]">Grid factor (kgCO₂e/kWh)</label><Input type="number" value={f.grid_factor_kg_kwh} onChange={(e) => num("grid_factor_kg_kwh", e.target.value)} /></div>
          <div className="col-span-2"><label className="label-eyebrow text-[10px]">Allocation method</label><Input value={f.allocation_method || ""} onChange={(e) => setF({ ...f, allocation_method: e.target.value })} placeholder="Mass-based allocation by product output" /></div>
        </div>
        <div className="flex justify-end mt-2"><Button variant="primary" onClick={save} disabled={busy} data-testid="cbam-prod-save">{busy ? "Saving…" : "Save product"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- case detail ----------------------------- */
function CaseDetail({ caseId, refData, onBack }) {
  const [c, setC] = useState(null);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [respText, setRespText] = useState("");
  const [newQuery, setNewQuery] = useState("");

  const load = useCallback(() => {
    api.get(`/cbam/cases/${caseId}`).then((r) => setC(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading case…</div>;

  const patch = async (body) => { try { const r = await api.patch(`/cbam/cases/${caseId}`, body); setC(r.data); } catch (e) { toast.error(apiErr(e)); } };
  const setStatus = async (status) => { try { const r = await api.put(`/cbam/cases/${caseId}/status`, { status }); setC(r.data); toast.success(`Status → ${STATUS_LABEL[status]}`); } catch (e) { toast.error(apiErr(e)); } };
  const setDoc = async (doc_id, status) => { try { const r = await api.put(`/cbam/cases/${caseId}/documents`, { doc_id, status }); setC(r.data); } catch (e) { toast.error(apiErr(e)); } };
  const delProduct = async (pid) => { try { const r = await api.delete(`/cbam/cases/${caseId}/products/${pid}`); setC(r.data); } catch (e) { toast.error(apiErr(e)); } };
  const aiApplicability = async () => { setAiBusy(true); try { const r = await api.post(`/cbam/cases/${caseId}/ai/applicability`); setAiText(r.data.assessment); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const aiResponse = async (query) => { setAiBusy(true); try { const r = await api.post(`/cbam/cases/${caseId}/ai/response`, { query }); setRespText(r.data.draft); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const addQuery = async () => { if (!newQuery) return; try { const r = await api.post(`/cbam/cases/${caseId}/queries`, { question: newQuery }); setC(r.data); setNewQuery(""); } catch (e) { toast.error(apiErr(e)); } };
  const resolveQuery = async (qid, response) => { try { const r = await api.put(`/cbam/cases/${caseId}/queries/${qid}`, { response: response || "Addressed in resubmitted pack." }); setC(r.data); toast.success("Query resolved"); } catch (e) { toast.error(apiErr(e)); } };
  const downloadPdf = () => { const token = localStorage.getItem("lestari_token"); fetch(`${API}/api/cbam/cases/${caseId}/pack.pdf`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "cbam_pack.pdf"; a.click(); URL.revokeObjectURL(u); }); };

  const t = c.totals || {};
  const a = c.applicability || {};
  const DOC_ICON = { ready: CheckCircle2, pending: Circle, na: MinusCircle };
  const DOC_COLOR = { ready: "text-[#00E65B]", pending: "text-muted-foreground", na: "text-muted-foreground/50" };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="cbam-case-detail">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2" data-testid="cbam-back-btn"><ArrowLeft className="h-3.5 w-3.5" /> All CBAM cases</button>
          <h1 className="font-display text-2xl font-bold tracking-tight">{c.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">{c.eu_principal?.name} · {c.eu_principal?.country} · {c.reporting_period}</div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={a.in_scope ? "default" : "outline"} data-testid="cbam-scope-badge">{a.in_scope ? "In scope" : "Scope pending"}</Badge>
          <Select value={c.status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="cbam-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(refData?.status_flow || []).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Products" value={t.products || 0} unit="" icon={Package} testid="cbam-stat-products" />
        <StatCard label="Total embedded" value={formatNum(t.total_embedded_t, { decimals: 1 })} unit="tCO₂e" icon={Leaf} testid="cbam-stat-embedded" />
        <StatCard label="Qty to EU" value={formatNum(t.qty_to_eu_t, { decimals: 0 })} unit="t" icon={Globe} testid="cbam-stat-qty" />
        <StatCard label="Evidence ready" value={`${t.docs_ready || 0}/${t.docs_total || 0}`} unit="" icon={FileText} testid="cbam-stat-docs" />
      </div>

      <Tabs defaultValue="applicability">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="applicability" data-testid="cbam-tab-applicability">Applicability</TabsTrigger>
          <TabsTrigger value="profile" data-testid="cbam-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="products" data-testid="cbam-tab-products">Products & Emissions</TabsTrigger>
          <TabsTrigger value="documents" data-testid="cbam-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="pack" data-testid="cbam-tab-pack">Data Pack</TabsTrigger>
          <TabsTrigger value="queries" data-testid="cbam-tab-queries">Queries {(c.queries || []).filter((q) => q.status === "open").length > 0 && <span className="ml-1 text-red-500">●</span>}</TabsTrigger>
        </TabsList>

        {/* Applicability */}
        <TabsContent value="applicability" className="mt-4 space-y-4">
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">Applicability checklist</div>
              <span className="text-xs font-mono text-muted-foreground">{a.completeness_pct}% complete</span>
            </div>
            <div className="space-y-2">
              {(a.checklist || []).map((ck, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {ck.met ? <CheckCircle2 className="h-4 w-4 text-[#00E65B]" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                  <span className={ck.met ? "" : "text-muted-foreground"}>{ck.item}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 border-t border-border pt-3">
              <div><label className="label-eyebrow text-[10px]">Exported to EU?</label>
                <Select value={String(a.exported_to_eu)} onValueChange={(v) => patch({ applicability: { exported_to_eu: v === "true" } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select></div>
              <div><label className="label-eyebrow text-[10px]">Data available?</label>
                <Select value={String(a.data_available)} onValueChange={(v) => patch({ applicability: { data_available: v === "true" } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select></div>
              <div><label className="label-eyebrow text-[10px]">Verification required?</label>
                <Select value={String(a.verification_required)} onValueChange={(v) => patch({ applicability: { verification_required: v === "true" } })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select></div>
              <div><label className="label-eyebrow text-[10px]">Primary CBAM sector</label>
                <Select value={a.cbam_sector || ""} onValueChange={(v) => patch({ applicability: { cbam_sector: v } })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(refData?.sector_names || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            {a.notes && <p className="text-xs text-muted-foreground mt-3">{a.notes}</p>}
          </div>
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI applicability assistant</div>
              <Button size="sm" variant="primary" onClick={aiApplicability} disabled={aiBusy} data-testid="cbam-ai-applicability">{aiBusy ? "Analysing…" : "Assess"}</Button>
            </div>
            {aiText ? <p className="text-sm leading-relaxed" data-testid="cbam-ai-applicability-text">{aiText}</p>
              : <p className="text-sm text-muted-foreground">Let AI assess CBAM relevance from your product CN codes and sector, and outline next steps.</p>}
          </div>
        </TabsContent>

        {/* Profile */}
        <TabsContent value="profile" className="mt-4 space-y-4">
          <div className="card-flat p-4 grid grid-cols-2 gap-3">
            {[["legal_entity", "Legal entity"], ["facility_name", "Facility name"], ["facility_address", "Facility address"], ["operator", "Operator"], ["contact", "Contact person"], ["products_supplied", "Products supplied"]].map(([k, label]) => (
              <div key={k}><label className="label-eyebrow text-[10px]">{label}</label>
                <Input defaultValue={c.profile?.[k] || ""} onBlur={(e) => e.target.value !== (c.profile?.[k] || "") && patch({ profile: { [k]: e.target.value } })} /></div>
            ))}
            <div className="col-span-2"><label className="label-eyebrow text-[10px]">Existing certifications</label>
              <Input defaultValue={(c.profile?.certifications || []).join(", ")} onBlur={(e) => patch({ profile: { certifications: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })} placeholder="ISO 14001, ISO 50001" /></div>
          </div>
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2">Carbon price disclosure</div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label-eyebrow text-[10px]">Local carbon price paid?</label>
                <Select value={String(c.carbon_price?.paid)} onValueChange={(v) => patch({ carbon_price: { paid: v === "true" } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select></div>
              <div><label className="label-eyebrow text-[10px]">Scheme</label><Input defaultValue={c.carbon_price?.scheme || ""} onBlur={(e) => patch({ carbon_price: { scheme: e.target.value } })} /></div>
              <div><label className="label-eyebrow text-[10px]">S$/tCO₂e</label><Input type="number" defaultValue={c.carbon_price?.amount_sgd_per_t || 0} onBlur={(e) => patch({ carbon_price: { amount_sgd_per_t: Number(e.target.value) } })} /></div>
            </div>
          </div>
        </TabsContent>

        {/* Products */}
        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">CBAM product master & embedded emissions</div>
            <ProductDialog caseId={caseId} refData={refData} onSaved={setC}
              trigger={<Button size="sm" variant="primary" data-testid="cbam-add-product"><Plus className="h-3.5 w-3.5" /> Add product</Button>} />
          </div>
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Product</TableHead><TableHead>CN code</TableHead><TableHead>Sector</TableHead>
                <TableHead className="text-right">Vol (t)</TableHead><TableHead className="text-right">To EU (t)</TableHead>
                <TableHead className="text-right">Direct/t</TableHead><TableHead className="text-right">Indirect/t</TableHead>
                <TableHead className="text-right">Embedded/t</TableHead><TableHead className="text-right">Total to EU</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(c.products || []).map((p) => (
                  <TableRow key={p.id} data-testid={`cbam-product-row-${p.id}`}>
                    <TableCell className="text-sm font-medium">{p.name}<div className="text-[10px] text-muted-foreground">{p.product_code}</div></TableCell>
                    <TableCell className="font-mono text-xs">{p.cn_code}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[9px]">{p.cbam_sector}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(p.production_volume_t, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(p.qty_to_eu_t, { decimals: 0 })}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{p.embedded_direct_t_per_t}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{p.embedded_indirect_t_per_t}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-bold">{p.embedded_total_t_per_t}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-bold text-accent">{p.total_embedded_to_eu_t}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <ProductDialog caseId={caseId} refData={refData} product={p} onSaved={setC}
                          trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><FileText className="h-3.5 w-3.5" /></Button>} />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => delProduct(p.id)} data-testid={`cbam-del-product-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!(c.products || []).length && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6 text-sm">No products yet. Add a CBAM-covered product to compute embedded emissions.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Direct embedded" value={formatNum(t.direct_t, { decimals: 1 })} unit="tCO₂e" icon={Factory} testid="cbam-total-direct" />
            <StatCard label="Indirect embedded" value={formatNum(t.indirect_t, { decimals: 1 })} unit="tCO₂e" icon={Factory} testid="cbam-total-indirect" />
            <StatCard label="Total to EU principal" value={formatNum(t.total_embedded_t, { decimals: 1 })} unit="tCO₂e" icon={Leaf} testid="cbam-total-embedded" />
          </div>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-4">
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow">CBAM evidence checklist</div>
              <div className="flex items-center gap-2 w-40"><Progress value={t.readiness_pct} className="h-1.5" /><span className="text-xs font-mono">{t.docs_ready}/{t.docs_total}</span></div>
            </div>
            <div className="space-y-1">
              {(c.documents || []).map((d) => {
                const Icon = DOC_ICON[d.status] || Circle;
                return (
                  <div key={d.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0" data-testid={`cbam-doc-${d.id}`}>
                    <div className="flex items-center gap-2"><Icon className={`h-4 w-4 ${DOC_COLOR[d.status]}`} /><span className="text-sm">{d.label}</span></div>
                    <Select value={d.status} onValueChange={(v) => setDoc(d.id, v)}>
                      <SelectTrigger className="w-28 h-7 text-xs" data-testid={`cbam-doc-status-${d.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="ready">Ready</SelectItem><SelectItem value="na">N/A</SelectItem></SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Data Pack */}
        <TabsContent value="pack" className="mt-4">
          <PackView caseId={caseId} onPdf={downloadPdf} />
        </TabsContent>

        {/* Queries */}
        <TabsContent value="queries" className="mt-4 space-y-4">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2">EU principal queries & resubmissions</div>
            <div className="flex gap-2 mb-3">
              <Input value={newQuery} onChange={(e) => setNewQuery(e.target.value)} placeholder="Log a query raised by the EU principal…" data-testid="cbam-query-input" />
              <Button variant="outline" onClick={addQuery} data-testid="cbam-add-query"><MessageSquare className="h-3.5 w-3.5" /> Log</Button>
            </div>
            <div className="space-y-2">
              {(c.queries || []).map((q) => (
                <div key={q.id} className="border border-border rounded-sm p-3" data-testid={`cbam-query-${q.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm">{q.question}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">{q.raised_by}</div>
                      {q.response && <div className="text-xs text-[#00E65B] mt-2">↳ {q.response}</div>}
                    </div>
                    <Badge variant={q.status === "open" ? "destructive" : "secondary"} className="text-[9px] shrink-0">{q.status}</Badge>
                  </div>
                  {q.status === "open" && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => aiResponse(q.question)} disabled={aiBusy} data-testid={`cbam-ai-response-${q.id}`}><Sparkles className="h-3.5 w-3.5 text-accent" /> AI draft</Button>
                      <Button size="sm" variant="outline" onClick={() => resolveQuery(q.id, respText)} data-testid={`cbam-resolve-${q.id}`}>Resolve</Button>
                    </div>
                  )}
                </div>
              ))}
              {!(c.queries || []).length && <div className="text-sm text-muted-foreground py-4 text-center">No queries from the EU principal.</div>}
            </div>
          </div>
          {respText && (
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI response draft</div>
              <p className="text-sm whitespace-pre-line" data-testid="cbam-response-draft">{respText}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PackView({ caseId, onPdf }) {
  const [pack, setPack] = useState(null);
  useEffect(() => { api.get(`/cbam/cases/${caseId}/pack`).then((r) => setPack(r.data)); }, [caseId]);
  if (!pack) return <div className="text-muted-foreground text-sm py-8 text-center">Assembling pack…</div>;
  return (
    <div className="space-y-3" data-testid="cbam-pack-view">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Supplier CBAM data pack — 10 sections</div>
        <Button variant="primary" size="sm" onClick={onPdf} data-testid="cbam-export-pdf"><FileDown className="h-3.5 w-3.5" /> Export PDF</Button>
      </div>
      {pack.missing_documents?.length > 0 && (
        <div className="card-flat p-3 border-l-2 border-l-[#FFB800] flex items-start gap-2" data-testid="cbam-pack-missing">
          <AlertTriangle className="h-4 w-4 text-[#FFB800] mt-0.5" />
          <div className="text-xs"><span className="font-medium">Outstanding evidence:</span> {pack.missing_documents.join(", ")}</div>
        </div>
      )}
      {pack.sections.map((s, i) => (
        <div key={i} className="card-flat p-4">
          <div className="font-display font-semibold text-sm mb-1">{i + 1}. {s.title}</div>
          {s.body && <p className="text-sm text-muted-foreground">{s.body}</p>}
          {Object.keys(s.fields || {}).length > 0 && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2">
              {Object.entries(s.fields).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs border-b border-border/50 py-1">
                  <span className="text-muted-foreground">{k}</span><span className="font-mono">{v ?? "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ----------------------------- list / dashboard ----------------------------- */
export default function CBAM() {
  const [ref, setRef] = useState(null);
  const [dash, setDash] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(() => { api.get("/cbam/dashboard").then((r) => setDash(r.data)).catch(() => setDash({ empty: true })); }, []);
  useEffect(() => { api.get("/cbam/reference").then((r) => setRef(r.data)); load(); }, [load]);

  if (active) return <CaseDetail caseId={active} refData={ref} onBack={() => { setActive(null); load(); }} />;
  if (!dash) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading CBAM…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="cbam-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Reporting & Compliance · CBAM</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Globe className="h-8 w-8 text-accent" /> CBAM Compliance</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Respond to EU principals' Carbon Border Adjustment Mechanism requests — assess applicability, calculate product embedded emissions, assemble the supplier data pack and manage queries.</p>
        </div>
        <CreateCaseDialog refData={ref} onCreated={(id) => { load(); setActive(id); }} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="CBAM cases" value={dash.total_cases || 0} unit="" icon={FileText} testid="cbam-dash-cases" />
        <StatCard label="In scope" value={dash.in_scope_cases || 0} unit="" icon={ShieldCheck} testid="cbam-dash-inscope" />
        <StatCard label="Embedded to EU" value={formatNum(dash.total_embedded_t, { decimals: 0 })} unit="tCO₂e" icon={Leaf} testid="cbam-dash-embedded" />
        <StatCard label="Products" value={dash.products || 0} unit="" icon={Package} testid="cbam-dash-products" />
        <StatCard label="Open queries" value={dash.open_queries || 0} unit="" icon={MessageSquare} testid="cbam-dash-queries" />
      </div>

      <div className="space-y-2">
        <div className="label-eyebrow">CBAM cases</div>
        {(dash.cases || []).map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} className="card-flat p-4 w-full text-left hover:border-accent/60 hover:bg-surface-hover transition-colors flex items-center justify-between gap-4" data-testid={`cbam-case-${c.id}`}>
            <div className="min-w-0">
              <div className="font-display font-semibold">{c.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.principal} · {c.country} · {c.reporting_period}</div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <div className="font-mono text-sm font-bold">{formatNum(c.totals?.total_embedded_t, { decimals: 1 })} <span className="text-muted-foreground font-sans text-xs">tCO₂e</span></div>
                <div className="text-[10px] text-muted-foreground">{c.totals?.docs_ready}/{c.totals?.docs_total} evidence</div>
              </div>
              {c.open_queries > 0 && <Badge variant="destructive" className="text-[9px]">{c.open_queries} query</Badge>}
              <Badge variant={STATUS_TONE[c.status] || "outline"} className="text-[10px]">{STATUS_LABEL[c.status] || c.status}</Badge>
            </div>
          </button>
        ))}
        {!(dash.cases || []).length && (
          <div className="card-flat p-10 text-center text-muted-foreground">
            No CBAM cases yet. Create one to start responding to an EU principal's request.
          </div>
        )}
      </div>
    </div>
  );
}
